/**
 * IntakePreview — a NO-LOGIN, READ-ONLY-DATA preview of the REAL intake forms.
 *
 * BATCH 1 item 6 (2026-06-25): the dev-harness HTML files are NOT reachable on a
 * deployed Firebase preview (the SPA rewrite `** → /index.html` swallows them and
 * vite's single-input build never copies them to dist). So the owner could not
 * click and SEE the real L2A / L2C forms. This route fixes that: it sits OUTSIDE
 * RequireAuth (public) and renders the SAME stage rail + the SAME IntakeModule
 * renderers the real `/intake` route uses — for any tier (`?tier=L2A`) — driven by
 * LOCAL form state only. No Firestore reads/writes, no client doc, no auth.
 *
 * It is a faithful VIEW of the forms, not a working intake (nothing saves). Staff
 * use the real `/intake` route; this is purely so the owner can review the form
 * shapes on the preview channel.
 *
 * Listeners inside some modules (e.g. the Coordination section's contact spine)
 * need auth and will simply yield an empty list here — the section still renders,
 * just without live contacts to search. That is expected for a visual preview.
 */
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { StageRail } from '../components/ui';
import {
  IntakeModule,
  type IntakeModuleHandlers,
} from '../components/IntakeModule';
import { resolveStages } from '../lib/intakeStages';
import { serviceLabel, spineTierKey } from '../lib/intakeCatalog';
import { previewRouteAllowed } from '../lib/previewHost';
import NotFound from './NotFound';
import {
  blankIntakeForm,
  newRowId,
  type IntakeForm,
  type ConcernInput,
  type PersonInput,
} from '../lib/intake';

const PREVIEW_TIERS = ['L1C', 'L2A', 'L2C', 'L1A', 'L1B', 'L2B', 'L2D', 'L3A', 'L3B'] as const;

export default function IntakePreview() {
  // GO-LIVE SAFETY (2026-06-25): this no-login review tool is gated to dev +
  // Firebase preview-channel hosts ONLY. On the LIVE domains it is NOT reachable
  // (renders the normal 404) — "no previews on the live domain".
  if (!previewRouteAllowed()) return <NotFound />;
  return <IntakePreviewInner />;
}

function IntakePreviewInner() {
  const [params, setParams] = useSearchParams();
  const tierParam = (params.get('tier') || 'L2A').trim();
  const tier = `${spineTierKey(tierParam)}_R1`;

  const stages = useMemo(() => resolveStages(tier), [tier]);
  const [activeIndex, setActiveIndex] = useState(0);
  const active = stages[Math.min(activeIndex, stages.length - 1)] ?? stages[0];

  const [form, setForm] = useState<IntakeForm>(() => blankIntakeForm());
  const [extras, setExtras] = useState<Record<string, Record<string, string | boolean>>>({});
  const patch = (p: Partial<IntakeForm>) => setForm((f) => ({ ...f, ...p }));

  const handlers: IntakeModuleHandlers = {
    patch,
    addConcern: (text = '') => patch({ presenting_concerns: [...form.presenting_concerns, { text, severity: '' }] }),
    setConcern: (i, c) => { const n = form.presenting_concerns.slice(); n[i] = { ...n[i], ...c } as ConcernInput; patch({ presenting_concerns: n }); },
    removeConcern: (i) => patch({ presenting_concerns: form.presenting_concerns.filter((_, j) => j !== i) }),
    addGoal: () => patch({ goals: [...form.goals, ''] }),
    setGoal: (i, v) => { const n = form.goals.slice(); n[i] = v; patch({ goals: n }); },
    removeGoal: (i) => patch({ goals: form.goals.filter((_, j) => j !== i) }),
    addPerson: () => patch({ persons: [...form.persons, { id: newRowId('p'), name: '', role: '' } as PersonInput] }),
    setPerson: (i, p) => { const n = form.persons.slice(); n[i] = { ...n[i], ...p }; patch({ persons: n }); },
    removePerson: (i) => patch({ persons: form.persons.filter((_, j) => j !== i) }),
    getExtra: (k, n) => extras[k]?.[n] ?? '',
    setExtra: (k, n, v) => setExtras((e) => ({ ...e, [k]: { ...(e[k] || {}), [n]: v } })),
  };

  const railStages = stages.map((s, i) => ({
    label: s.title,
    state: (i < activeIndex ? 'done' : i === activeIndex ? 'now' : 'todo') as 'done' | 'now' | 'todo',
  }));

  return (
    <div style={{ maxWidth: 920, margin: '0 auto', padding: '20px 16px 80px' }}>
      <div style={{ background: 'var(--warn-wash, #fbf3e1)', border: '1px solid var(--warn-line, #eccf93)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12.5, color: 'var(--warn-deep, #9a6a12)' }}>
        <b>Preview mode (no login).</b> This is a faithful VIEW of the real intake form for owner review — nothing you type is saved.
        Staff fill the real form inside the portal. Switch tiers below to compare.
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <span style={{ fontSize: 12.5, fontWeight: 600 }}>Service:</span>
        <select
          value={spineTierKey(tierParam)}
          onChange={(e) => { setParams({ tier: e.target.value }); setActiveIndex(0); }}
          style={{ border: '1px solid var(--rule-strong, #cdd4da)', borderRadius: 8, padding: '7px 10px', fontSize: 13 }}
        >
          {PREVIEW_TIERS.map((t) => <option key={t} value={t}>{serviceLabel(t)}</option>)}
        </select>
      </div>

      <h2 style={{ fontSize: 18, margin: '0 0 4px' }}>{serviceLabel(tier)}</h2>
      <p style={{ fontSize: 12.5, color: 'var(--ink-mute)', margin: '0 0 16px' }}>{active?.blurb}</p>

      <StageRail stages={railStages} />

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '14px 0' }}>
        {stages.map((s, i) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setActiveIndex(i)}
            style={{
              border: '1px solid var(--rule)', borderRadius: 999, padding: '6px 12px', fontSize: 12.5, cursor: 'pointer',
              background: i === activeIndex ? 'var(--ink, #1a1f24)' : 'var(--surface, #fff)',
              color: i === activeIndex ? '#fff' : 'var(--ink)', fontWeight: i === activeIndex ? 600 : 400,
            }}
          >
            {i + 1}. {s.title}
          </button>
        ))}
      </div>

      <div>
        {active?.consent ? (
          <div style={{ border: '1px solid var(--rule)', borderRadius: 10, padding: 16, fontSize: 13, color: 'var(--ink-mute)' }}>
            <b style={{ color: 'var(--ink)' }}>Review &amp; submit</b> — the final tab holds the consent / privacy tick-boxes and the
            "Review &amp; print summary" button. (Consent UI is rendered in the real form; omitted in this preview.)
          </div>
        ) : (
          active?.moduleKeys.map((key) => (
            <IntakeModule
              key={key}
              moduleKey={key}
              form={form}
              readOnly={false}
              handlers={handlers}
              viewerIsStaff
              serviceTier={tier}
              propertyRegistry={undefined}
            />
          ))
        )}
      </div>
    </div>
  );
}
